import { supabase } from './supabase';
import { logger } from './logger';

const MUSIC_BUCKET = 'music-tracks';
const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 MB

export interface MusicTrack {
  id: string;
  title: string;
  artist: string;
  storagePath: string;
  fileUrl: string;
  durationSeconds: number;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
}

function mapRow(r: any): MusicTrack {
  return {
    id: r.id,
    title: r.title || '',
    artist: r.artist || '',
    storagePath: r.storage_path || '',
    fileUrl: r.file_url || '',
    durationSeconds: r.duration_seconds || 0,
    isActive: r.is_active ?? true,
    sortOrder: r.sort_order || 0,
    createdAt: r.created_at,
  };
}

export async function fetchMusicTracks(): Promise<MusicTrack[]> {
  const { data, error } = await supabase
    .from('music_tracks')
    .select('*')
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: false });

  if (error) {
    logger.error('fetch_music_tracks', 'musicService', { error: error.message });
    throw error;
  }
  logger.technical('fetch_music_tracks_ok', 'musicService', { count: (data || []).length });
  return (data || []).map(mapRow);
}

export async function createMusicTrack(input: {
  title: string;
  artist?: string;
  file: File;
  durationSeconds: number;
}): Promise<MusicTrack> {
  if (input.file.size > MAX_FILE_SIZE) {
    throw new Error('Fichier trop volumineux (max 15 Mo)');
  }

  const ext = input.file.name.split('.').pop()?.toLowerCase() || 'mp3';
  const storagePath = `${crypto.randomUUID()}.${ext}`;
  const contentType = input.file.type || 'audio/mpeg';

  logger.functional('create_music_track', 'musicService', { title: input.title });

  const { error: uploadError } = await supabase.storage
    .from(MUSIC_BUCKET)
    .upload(storagePath, input.file, { contentType, upsert: false });

  if (uploadError) {
    logger.error('upload_music_file', 'musicService', { error: uploadError.message });
    throw uploadError;
  }

  const { data: urlData } = supabase.storage.from(MUSIC_BUCKET).getPublicUrl(storagePath);

  const { data, error } = await supabase
    .from('music_tracks')
    .insert({
      title: input.title,
      artist: input.artist || null,
      storage_path: storagePath,
      file_url: urlData.publicUrl,
      duration_seconds: Math.round(input.durationSeconds),
    })
    .select()
    .single();

  if (error) {
    logger.error('create_music_track', 'musicService', { error: error.message });
    // Nettoyage du fichier orphelin
    await supabase.storage.from(MUSIC_BUCKET).remove([storagePath]).catch(() => {});
    throw error;
  }

  return mapRow(data);
}

export async function updateMusicTrack(
  id: string,
  updates: Partial<Pick<MusicTrack, 'title' | 'artist' | 'isActive' | 'sortOrder'>>,
): Promise<void> {
  const row: Record<string, any> = {};
  if (updates.title !== undefined) row.title = updates.title;
  if (updates.artist !== undefined) row.artist = updates.artist || null;
  if (updates.isActive !== undefined) row.is_active = updates.isActive;
  if (updates.sortOrder !== undefined) row.sort_order = updates.sortOrder;

  logger.functional('update_music_track', 'musicService', { id, fields: Object.keys(row) });

  const { error } = await supabase.from('music_tracks').update(row).eq('id', id);
  if (error) {
    logger.error('update_music_track', 'musicService', { id, error: error.message });
    throw error;
  }
}

export async function deleteMusicTrack(track: MusicTrack): Promise<void> {
  logger.functional('delete_music_track', 'musicService', { id: track.id, title: track.title });

  const { error } = await supabase.from('music_tracks').delete().eq('id', track.id);
  if (error) {
    logger.error('delete_music_track', 'musicService', { id: track.id, error: error.message });
    throw error;
  }

  if (track.storagePath) {
    await supabase.storage.from(MUSIC_BUCKET).remove([track.storagePath]).catch(() => {});
  }
}
